import React, { useState, useEffect } from 'react';
import './utils/featureFlags';
import { AtticusHeader } from './components/AtticusHeader';
import { LiquidityDisplay } from './components/LiquidityDisplay';
import { LiveChart } from './components/LiveChart';
import { ExpirySelector } from './components/ExpirySelector';
import { OptionTypeSelector } from './components/OptionTypeSelector';
import { StrikeCards } from './components/StrikeCards';
import { OrderSection } from './components/OrderSection';
import { BottomNav } from './components/BottomNav';
import { AutoHedgeModal } from './components/AutoHedgeModal';
import { EducationalDemo } from './components/EducationalDemo';
import TournamentJoinModal from './components/TournamentJoinModal';
import { PositionDetailsModal } from './components/PositionDetailsModal';
import { HistoryModal } from './components/HistoryModal';
import { ComingSoonModal } from './components/ComingSoonModal';
import SocialTradingModal from './components/SocialTradingModal';
import LeaderboardModal from './components/LeaderboardModal';
import { usePricing } from './contexts/PricingContext';
import { useDemoMode } from './contexts/DemoModeContext';
import { FEATURES, getFeatureFlag } from './utils/featureFlags';
import { SocialTradingProvider } from './contexts/SocialTradingContext';
import { TournamentProvider } from './contexts/TournamentContext';
import { AutoHedgeProvider } from './contexts/AutoHedgeContext';
import { PricingProvider } from './contexts/PricingContext';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { Principal } from '@dfinity/principal';
import { Actor, HttpAgent } from '@dfinity/agent';
import { createAgent, getCanisterId, logNetworkConfig } from './utils/icConfig';

// Candid interface for social trading integration
const idlFactory = ({ IDL }) => {
  const TradeData = IDL.Record({
    'option_type': IDL.Text,
    'strike_price': IDL.Float64,
    'premium': IDL.Float64,
    'quantity': IDL.Nat32,
    'side': IDL.Text,
    'btc_price': IDL.Float64,
    'expiry_time': IDL.Nat64,
  });

  const UserProfile = IDL.Record({
    'user_id': IDL.Text,
    'display_name': IDL.Text,
    'xp_points': IDL.Nat32,
    'completed_tutorials': IDL.Vec(IDL.Text),
    'skill_level': IDL.Text,
  });

  const CompetitionRegistrationRequest = IDL.Record({
    'user_id': IDL.Text,
    'competition_id': IDL.Text,
    'display_name': IDL.Text,
    'strategy_description': IDL.Text,
  });

  const TradeRecord = IDL.Record({
    'user_id': IDL.Text,
    'competition_id': IDL.Text,
    'trade_id': IDL.Text,
    'timestamp': IDL.Nat64,
    'option_type': IDL.Text,
    'strike_price': IDL.Float64,
    'premium': IDL.Float64,
    'quantity': IDL.Nat32,
    'side': IDL.Text,
    'btc_price_at_entry': IDL.Float64,
    'expiry_time': IDL.Nat64,
    'status': IDL.Text,
    'pnl': IDL.Float64,
  });

  const CompetitionPortfolio = IDL.Record({
    'user_id': IDL.Text,
    'competition_id': IDL.Text,
    'current_balance': IDL.Float64,
    'starting_balance': IDL.Float64,
    'open_positions': IDL.Vec(TradeRecord),
    'closed_trades': IDL.Vec(TradeRecord),
    'total_pnl': IDL.Float64,
    'total_trades': IDL.Nat32,
    'win_rate': IDL.Float64,
    'last_updated': IDL.Nat64,
  });

  return IDL.Service({
    'initialize_social_trading_system': IDL.Func([], [IDL.Text], []),
    'record_demo_trade_for_competition': IDL.Func([IDL.Text, IDL.Text, TradeData], [IDL.Text], []),
    'record_competition_trade': IDL.Func([TradeRecord], [IDL.Text], []),
    'get_user_competition_portfolio': IDL.Func([IDL.Text, IDL.Text], [IDL.Opt(CompetitionPortfolio)], ['query']),
    'get_competition_trades': IDL.Func([IDL.Text], [IDL.Vec(TradeRecord)], ['query']),
    'get_user_active_competitions': IDL.Func([IDL.Text], [IDL.Vec(IDL.Record({
      '0': IDL.Text, // TradingCompetition placeholder
      '1': IDL.Opt(CompetitionPortfolio)
    }))], ['query']),
    'create_user_profile': IDL.Func([UserProfile], [IDL.Text], []),
    'register_for_competition': IDL.Func([CompetitionRegistrationRequest], [IDL.Record({
      'success': IDL.Bool,
      'registration_confirmed': IDL.Bool,
      'error': IDL.Opt(IDL.Text),
    })], []),
    'track_user_session': IDL.Func([IDL.Text, IDL.Nat64], [IDL.Text], []),
    'get_active_competitions': IDL.Func([], [IDL.Vec(IDL.Text)], ['query']), // Simplified for now
  });
};

function AppContent() {
  const [selectedType, setSelectedType] = useState('call');
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('positions');
  const [isAutoHedgeOpen, setIsAutoHedgeOpen] = useState(false);
  const [isEducationalDemoOpen, setIsEducationalDemoOpen] = useState(false);
  const [isSocialTradingOpen, setIsSocialTradingOpen] = useState(false);
  const [isTournamentJoinOpen, setIsTournamentJoinOpen] = useState(false);
  const [isPositionDetailsOpen, setIsPositionDetailsOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('risk');
  const [socialTradingCanisterId, setSocialTradingCanisterId] = useState(null);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);

  // CONTEXT HOOKS
  const { btcPrice } = usePricing();
  const { isDemoMode, demoPortfolio, addDemoTrade, updateDemoPortfolio } = useDemoMode();

  // WELCOME POPUP STATE - RESTORED TO SHOW ON PAGE LOAD
  const [showWelcomePopup, setShowWelcomePopup] = useState(true);
  const [inWalkthrough, setInWalkthrough] = useState(false);
  const [step, setStep] = useState(0);

  // DRAGGABLE FUNCTIONALITY STATE
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // GUIDED TRADING STATE
  const [guidedTradeCompleted, setGuidedTradeCompleted] = useState(false);
  const [demoTradeResult, setDemoTradeResult] = useState(null);
  const [buyOptionSelected, setBuyOptionSelected] = useState(false);
  const [walkthroughOptionSelected, setWalkthroughOptionSelected] = useState(false);
  const [walkthroughStrikeSelected, setWalkthroughStrikeSelected] = useState(false);

  // MINIMIZABLE WALKTHROUGH STATE
  const [isWalkthroughMinimized, setIsWalkthroughMinimized] = useState(false);

  // NEW: Tournament tracking state
  const [activeUserCompetitions, setActiveUserCompetitions] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [showUserLogin, setShowUserLogin] = useState(false);

  // Initialize social trading canister ID and user ID
  React.useEffect(() => {
    // Log network configuration on startup
    logNetworkConfig();
    
    // Get social trading canister ID from network config (mainnet or local)
    const socialTradingId = getCanisterId('social_trading');
    if (socialTradingId) {
      setSocialTradingCanisterId(socialTradingId);
      console.log('✅ Social trading canister ID loaded:', socialTradingId);
    } else {
      console.warn('⚠️ Social trading canister ID not available');
    }
    
    // Check if user is already logged in
    const savedUserId = localStorage.getItem('atticus_user_id');
    const savedDisplayName = localStorage.getItem('atticus_display_name');
    
    if (savedUserId && savedDisplayName) {
      setCurrentUserId(savedUserId);
      setUserDisplayName(savedDisplayName);
    } else {
      // Generate anonymous user ID for demo mode
      const anonymousId = `demo_user_${Date.now()}`;
      setCurrentUserId(anonymousId);
      setUserDisplayName('Demo Trader');
      localStorage.setItem('atticus_user_id', anonymousId);
      localStorage.setItem('atticus_display_name', 'Demo Trader');
    }
  }, []);
  
  // Load active competitions when canister ID is set
  React.useEffect(() => {
    if (socialTradingCanisterId && currentUserId) {
      loadActiveCompetitions().catch(err => {
        console.log('Social trading backend not available:', err.message);
      });
    }
  }, [socialTradingCanisterId, currentUserId]);
  
  // Load active competitions for tournament tracking
  const loadActiveCompetitions = async () => {
    if (!socialTradingCanisterId) return;
    
    try {
      // Create agent with proper network detection (mainnet or local)
      const agent = await createAgent(HttpAgent);
      
      const actor = Actor.createActor(idlFactory, {
        agent,
        canisterId: Principal.fromText(socialTradingCanisterId)
      });

      // Initialize social trading system first
      try {
        await actor.initialize_social_trading_system();
        console.log('Social trading system initialized');
      } catch (error) {
        console.log('Social trading system may already be initialized:', error);
      }

      // Get active competitions
      const competitions = await actor.get_active_competitions();
      console.log('Active competitions loaded:', competitions);
      
      // For demo purposes, auto-register user for first competition if available
      if (competitions.length > 0 && currentUserId) {
        const firstCompetition = competitions[0];
        try {
          await actor.register_for_competition({
            user_id: currentUserId,
            competition_id: firstCompetition,
            display_name: userDisplayName || 'Demo Trader',
            strategy_description: 'Learning options trading through demo mode'
          });
          setActiveUserCompetitions([firstCompetition]);
          console.log('Auto-registered for competition:', firstCompetition);
        } catch (error) {
          console.log('Auto-registration failed (may already be registered):', error);
        }
      }
    } catch (error) {
      console.error('Failed to load active competitions:', error);
    }
  };

  // DEMO TRADE HANDLER WITH BALANCE & POSITION TRACKING
  const handleDemoTrade = (tradeData) => {
    console.log("DEMO TRADE HANDLER CALLED:", tradeData);
    
    if (isDemoMode) {
      try {
        // Calculate trade amount (no commission in user display)
        const tradeAmount = tradeData.premium * tradeData.quantity;
        const isSell = tradeData.side === "sell";
        const balanceChange = isSell ? tradeAmount : -tradeAmount; // Credit for sell, debit for buy
        
        const demoTrade = {
          ...tradeData,
          id: Date.now(),
          timestamp: new Date().toISOString(),
          currentPrice: btcPrice,
          entryPrice: btcPrice,
          status: isSell ? "short" : "long",
          expiry: tradeData.expiry || new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(), // Use actual trade expiry
          unrealizedPnl: 0
        };
        
        console.log("Demo trade executing:", demoTrade);
        console.log("Current balance:", demoPortfolio.balance);
        console.log("Balance change:", balanceChange, isSell ? "(CREDIT)" : "(DEBIT)");
        
        // Add trade to portfolio and positions
        addDemoTrade(demoTrade);
        
        // Add to positions if opening new position
        const currentPositions = demoPortfolio.positions || [];
        console.log('TRADE DATA FLOW DEBUG:', {
          'STEP 1 - demoTrade created': demoTrade,
          'STEP 2 - currentPositions before': currentPositions,
          'STEP 3 - demoPortfolio.positions': demoPortfolio.positions
        });
      
        const updatedPositions = [...currentPositions, demoTrade];
      
        console.log('TRADE DATA FLOW DEBUG CONTINUED:', {
          'STEP 4 - updatedPositions after': updatedPositions,
          'STEP 5 - About to call updateDemoPortfolio with': { positions: updatedPositions }
        });
        
        console.log('DEBUG: Position being stored:', {
          demoTrade: demoTrade,
          currentPositions: currentPositions,
          updatedPositions: updatedPositions
        });
        
        // Update demo balance (credit for sell, debit for buy)
        const newBalance = demoPortfolio.balance + balanceChange;
        updateDemoPortfolio({ 
          positions: updatedPositions,
          balance: newBalance 
        });
        
        console.log("New balance:", newBalance);
        
        // Show success feedback (no commission mentioned)
        alert('Demo Trade Executed!\n' +
          tradeData.side.toUpperCase() + ' ' +
          tradeData.quantity + ' ' +
          tradeData.type.toUpperCase() + ' contract' +
          (tradeData.quantity > 1 ? 's' : '') + '\n' +
          'Strike: $' + tradeData.strike.toLocaleString() + '\n' +
          'Premium: $' + tradeData.premium.toLocaleString() + ' per contract\n' +
          'Total ' + (isSell ? 'Credit' : 'Cost') + ': $' + tradeAmount.toLocaleString() + '\n' +
          'Status: FILLED');
        
        return demoTrade;
      } catch (error) {
        console.error("Demo trade error:", error);
        alert("Demo trade failed. Check console for details.");
      }
    } else {
      console.log("Live trading not implemented");
      alert("Live trading will be available when platform goes live. Currently in demo mode.");
    }
  };

  // RESTORED: Educational Demo handler - opens Academy modal
  const handleEducationalDemoClick = () => {
    console.log('Educational Demo clicked - Opening Academy');
    
    // Open the educational demo modal (Academy)
    setIsEducationalDemoOpen(true);
    
    // Don't start walkthrough - that's separate
    setShowWelcomePopup(false);
    setInWalkthrough(false);
  };

  // NEW: Walkthrough handler - starts guided walkthrough
  const handleWalkthroughClick = () => {
    console.log('Walkthrough clicked - Starting guided tutorial');
    
    // Reset all walkthrough states
    setInWalkthrough(false);
    setStep(0);
    setGuidedTradeCompleted(false);
    setWalkthroughOptionSelected(false);
    setWalkthroughStrikeSelected(false);
    setBuyOptionSelected(false);
    setIsWalkthroughMinimized(false);
    
    // Show welcome popup and start walkthrough immediately
    setShowWelcomePopup(true);
    setInWalkthrough(true);
    setStep(0);
    
    // Don't open the educational demo modal
    setIsEducationalDemoOpen(false);
  };

  const handleEducationalDemoClose = () => {
    console.log('Educational Demo closed');
    setIsEducationalDemoOpen(false);
  };

  const handleSocialTradingClick = () => {
    console.log('Social Trading clicked');
    if (getFeatureFlag('SOCIAL_TRADING')) {
      setIsTournamentJoinOpen(true);
    } else {
      alert('Social trading features are currently disabled. Enable in feature flags.');
    }
  };

  const handleSocialTradingClose = () => {
    console.log('Social Trading closed');
    setIsSocialTradingOpen(false);
  };

  // NEW: Handle tournament registration updates
  const handleTournamentRegistration = (competitionId) => {
    console.log('Tournament registration for:', competitionId);
    if (!activeUserCompetitions.includes(competitionId)) {
      setActiveUserCompetitions(prev => {
        const updated = [...prev, competitionId];
        console.log('Updated active competitions:', updated);
        return updated;
      });
    }
  };

  const demoSteps = [
    {
      title: 'BTC Current Price',
      content: 'This shows Bitcoin\'s current market price. This is the foundation for all options pricing.',
      calculation: 'Live BTC price feeds into all option calculations - see chart above',
      type: 'overview'
    },
    {
      title: 'Put vs Call Options',
      content: 'Call = Right to BUY Bitcoin. Put = Right to SELL Bitcoin. Choose based on market direction.',
      calculation: 'Calls profit when BTC rises above strike. Puts profit when BTC falls below strike.',
      type: 'overview'
    },
    {
      title: 'Strike Price Selection',
      content: 'Choose the price at which you can buy/sell Bitcoin if you exercise the option.',
      calculation: 'If BTC > Strike Price, call options gain value. If BTC < Strike Price, put options gain value.',
      type: 'overview'
    },
    {
      title: 'Option Premium',
      content: 'The cost to buy this option contract. This is what you pay upfront.',
      calculation: 'Premium = Intrinsic Value + Time Value + Volatility Premium',
      type: 'overview'
    },
    {
      title: 'Select Your Option Type',
      content: 'Let\'s start trading! Choose CALL if you think BTC will go up, or PUT if you think it will go down.',
      calculation: 'Current BTC price shown in chart above - try selecting CALL (beginner friendly)',
      type: 'interactive',
      action: 'Tap CALL or PUT below',
      requiresSelection: 'optionType'
    },
    {
      title: 'Choose Your Strike Price',
      content: 'Now select a strike price. For practice, try selecting a strike close to the current BTC price.',
      calculation: 'Tap on one of the strike price cards below',
      type: 'interactive',
      action: 'Tap a strike price card',
      requiresSelection: 'strike'
    },
    {
      title: 'Choose Buy Option',
      content: 'Options can be bought OR sold. For beginners, we\'ll focus on BUYING options only.',
      calculation: 'Buying options = Limited risk (only lose premium paid). Selling options = Unlimited risk (not recommended for beginners).',
      type: 'interactive',
      action: 'We\'ll use BUY for this demo',
      requiresSelection: 'buyAction',
      beginner: true
    },
    {
      title: 'Execute Your Demo Trade',
      content: 'Perfect! Now execute your first demo trade. This will use virtual money to practice.',
      calculation: 'Complete option type and strike selection to see trade details',
      type: 'interactive',
      action: 'Tap the BUY button below',
      requiresSelection: 'execution'
    },
    {
      title: 'Position Monitoring',
      content: 'Complete your trade to see position monitoring',
      calculation: 'Execute a trade to see your position monitoring with live market data',
      type: 'monitoring',
      celebration: guidedTradeCompleted
    }
  ];

  const handleDragStart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;

    const maxX = window.innerWidth - 380;
    const maxY = window.innerHeight - 350;

    setDragPosition({
      x: Math.max(0, Math.min(maxX, newX)),
      y: Math.max(0, Math.min(maxY, newY))
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Auto-advance walkthrough steps
  React.useEffect(() => {
    if (inWalkthrough && step === 4 && walkthroughOptionSelected && selectedType) {
      setTimeout(() => setStep(5), 1000);
    }
    if (inWalkthrough && step === 5 && walkthroughStrikeSelected && selectedStrike) {
      setTimeout(() => setStep(6), 1000);
    }
    if (inWalkthrough && step === 6 && buyOptionSelected) {
      setTimeout(() => setStep(7), 1000);
    }
    if (inWalkthrough && step === 7 && guidedTradeCompleted) {
      setTimeout(() => setStep(8), 2000);
    }
  }, [inWalkthrough, step, walkthroughOptionSelected, walkthroughStrikeSelected, buyOptionSelected, guidedTradeCompleted, selectedType, selectedStrike]);

  function canAdvanceInteractiveStep() {
    if (step === 4) return walkthroughOptionSelected && selectedType;
    if (step === 5) return walkthroughStrikeSelected && selectedStrike;
    if (step === 6) return buyOptionSelected;
    if (step === 7) return guidedTradeCompleted;
    return true;
  }

  // Notifications for BottomNav badges
  const notifications = {
    history: demoPortfolio.trades?.length > 0 ? demoPortfolio.trades.length : undefined,
    analysis: undefined,
    positions: isDemoMode && demoPortfolio.positions?.length > 0 ? demoPortfolio.positions.length : undefined
  };

  return (
    <>
      <SocialTradingProvider canisterId={socialTradingCanisterId} userId={currentUserId}>
        <TournamentProvider canisterId={socialTradingCanisterId} userId={currentUserId} userDisplayName={userDisplayName}>
        <style jsx>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          .walkthrough-highlight {
            animation: pulse 2s infinite;
          }
        `}</style>

      {/* USER LOGIN POPUP */}
      {showUserLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4">
          <div className="bg-atticus-navy-950 rounded-lg border border-yellow-400/30 p-6 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-yellow-400 mb-4">
              Welcome to Atticus Trade
            </h2>
            <p className="text-gray-300 mb-6">
              Enter your display name to track your trading progress and compete in tournaments:
            </p>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Enter your display name"
                value={userDisplayName}
                onChange={(e) => setUserDisplayName(e.target.value)}
                className="w-full px-4 py-3 bg-atticus-navy-800 border border-yellow-400/30 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                maxLength={20}
              />
              <button 
                onClick={() => {
                  if (userDisplayName.trim()) {
                    const userId = 'user_' + Date.now();
                    setCurrentUserId(userId);
                    localStorage.setItem('atticus_user_id', userId);
                    localStorage.setItem('atticus_display_name', userDisplayName.trim());
                    setShowUserLogin(false);
                    setShowWelcomePopup(true);
                  } else {
                    alert('Please enter a display name');
                  }
                }}
                disabled={!userDisplayName.trim()}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-medium py-3 px-4 rounded-md hover:from-yellow-300 hover:to-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Trading
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ENHANCED WELCOME POPUP - TWO BUTTONS */}
      {showWelcomePopup && !inWalkthrough && !showUserLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4">
          <div className="bg-atticus-navy-950 rounded-lg border border-yellow-400/30 p-6 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-yellow-400 mb-4">
              Professional BTC Options Trading Platform
            </h2>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm border border-green-400/30">
                Demo Mode Active
              </span>
              {userDisplayName && (
                <div className="mt-2">
                  <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm border border-blue-400/30">
                    Welcome back, {userDisplayName}!
                  </span>
                </div>
              )}
            </div>
            <p className="text-gray-300 mb-6">
              Choose how you'd like to explore our platform:
            </p>
            <div className="space-y-3">
              <button 
                onClick={handleWalkthroughClick}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-medium py-3 px-4 rounded-md hover:from-yellow-300 hover:to-yellow-400 transition-colors"
              >
                🎯 Start Guided Walkthrough
              </button>
              <button 
                onClick={() => {
                  setShowWelcomePopup(false);
                  setIsEducationalDemoOpen(true);
                }}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium py-3 px-4 rounded-md hover:from-blue-400 hover:to-blue-500 transition-colors"
              >
                📚 Open Trading Academy
              </button>
              <button 
                onClick={() => setShowWelcomePopup(false)}
                className="w-full bg-atticus-navy-800 text-yellow-400 border border-yellow-400/30 font-medium py-3 px-4 rounded-md hover:bg-yellow-400/10 transition-colors"
              >
                🚀 Explore Freely
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DRAGGABLE GUIDED WALKTHROUGH POPUP */}
      {showWelcomePopup && inWalkthrough && (
        <div 
          className={`fixed bg-atticus-navy-950 rounded-lg border border-yellow-400/30 p-4 z-50 shadow-2xl transition-all duration-200 ${
            isWalkthroughMinimized ? 'w-64 h-16' : 'w-80 max-h-96'
          }`}
          style={{
            left: `${dragPosition.x}px`,
            top: `${dragPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
            backgroundColor: 'rgba(15, 23, 42, 0.98)'
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onMouseMove={handleDragMove}
          onTouchMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onTouchEnd={handleDragEnd}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-yellow-400 text-sm font-medium">
                Step {step + 1}/9
              </span>
              <span className="text-xs text-gray-400">
                {demoSteps[step]?.type === 'interactive' ? '🎯 Interactive' : 
                 demoSteps[step]?.type === 'monitoring' ? '📊 Monitor' : '👀 Learn'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setIsWalkthroughMinimized(!isWalkthroughMinimized)}
                className="text-gray-400 hover:text-white text-sm p-1"
                title={isWalkthroughMinimized ? "Expand" : "Minimize"}
              >
                {isWalkthroughMinimized ? '📖' : '📕'}
              </button>
              <button 
                onClick={() => {
                  setShowWelcomePopup(false);
                  setInWalkthrough(false);
                }}
                className="text-gray-400 hover:text-white text-sm px-2 py-1 rounded border border-gray-600 hover:border-gray-500"
                title="Hide Walkthrough"
              >
                Hide
              </button>
            </div>
          </div>

          {isWalkthroughMinimized ? (
            <div className="text-sm text-gray-300">
              {demoSteps[step]?.title} - Click 📖 to expand
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-yellow-400">
                {demoSteps[step]?.title}
              </h3>
              
              <div className="text-sm text-gray-300 leading-relaxed">
                {demoSteps[step]?.content}
              </div>

              <div className="bg-atticus-navy-900 rounded p-3 text-xs text-gray-400 border border-yellow-400/20">
                {demoSteps[step]?.calculation}
              </div>

              {step === 4 && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-400">
                    Click on CALL or PUT in the option type selector above, then confirm below:
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        setWalkthroughOptionSelected(true);
                        setSelectedType('call');
                      }}
                      className={`flex-1 py-2 px-3 text-xs rounded transition-colors ${
                        selectedType === 'call' && walkthroughOptionSelected
                          ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                          : 'bg-atticus-navy-800 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/10'
                      }`}
                    >
                      📈 CALL Selected
                    </button>
                    <button 
                      onClick={() => {
                        setWalkthroughOptionSelected(true);
                        setSelectedType('put');
                      }}
                      className={`flex-1 py-2 px-3 text-xs rounded transition-colors ${
                        selectedType === 'put' && walkthroughOptionSelected
                          ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                          : 'bg-atticus-navy-800 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/10'
                      }`}
                    >
                      📉 PUT Selected
                    </button>
                  </div>
                </div>
              )}

              {step === 5 && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-400">
                    Click on any strike price card in the highlighted section above, then confirm below:
                  </div>
                  <button 
                    onClick={() => setWalkthroughStrikeSelected(true)}
                    className={`w-full py-2 px-3 text-xs rounded transition-colors ${
                      walkthroughStrikeSelected
                        ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                        : 'bg-yellow-400 text-black hover:bg-yellow-300'
                    }`}
                  >
                    {walkthroughStrikeSelected ? '✅ Strike Price Selected' : '🎯 Confirm Strike Selection'}
                  </button>
                </div>
              )}

              {step === 6 && (
                <div className="space-y-2">
                  <div className="bg-blue-500/10 border border-blue-400/30 rounded p-2 text-xs">
                    <div className="text-blue-400 font-medium mb-1">**Why BUY only?**</div>
                    <div className="text-gray-300">
                      • **Buying** = Limited risk (only lose what you pay)<br/>
                      • **Selling** = Unlimited risk (advanced strategy)
                    </div>
                  </div>
                  <button 
                    onClick={() => setBuyOptionSelected(true)}
                    className={`w-full py-2 px-3 text-xs rounded transition-colors ${
                      buyOptionSelected
                        ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                        : 'bg-yellow-400 text-black hover:bg-yellow-300'
                    }`}
                  >
                    {buyOptionSelected ? '✅ Got it! BUY for safety' : '🛡️ Understand: BUY = Limited Risk'}
                  </button>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button 
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="px-3 py-1 text-xs bg-atticus-navy-800 text-gray-400 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-atticus-navy-700 transition-colors"
                >
                  ← Back
                </button>
                
                <div className="flex gap-1">
                  {demoSteps.map((_, index) => (
                    <div 
                      key={index}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === step ? 'bg-yellow-400' : 
                        index < step ? 'bg-green-400' : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                <button 
                  onClick={() => {
                    if (step < demoSteps.length - 1) {
                      if (demoSteps[step]?.type === 'interactive' && !canAdvanceInteractiveStep()) {
                        alert(`Please complete the required action: ${demoSteps[step]?.action || 'Complete the step above'}`);
                        return;
                      }
                      setStep(step + 1);
                    } else {
                      setShowWelcomePopup(false);
                      setInWalkthrough(false);
                      alert('🎉 Walkthrough completed! You\'re now ready to explore the platform. Try the Social Trading features!');
                    }
                  }}
                  disabled={step === demoSteps.length - 1 && !guidedTradeCompleted}
                  className="px-3 py-1 text-xs bg-yellow-400 text-black rounded hover:bg-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {step === demoSteps.length - 1 ? 'Finish' : 'Next →'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="min-h-screen bg-atticus-navy-950">
        <AtticusHeader 
          onAutoHedgeClick={() => setIsAutoHedgeOpen(true)}
          onEducationalDemoClick={handleEducationalDemoClick}
          onWalkthroughClick={handleWalkthroughClick}
          onSocialTradingClick={() => setIsSocialTradingOpen(true)}
          onLeaderboardClick={() => setIsLeaderboardOpen(true)}
          socialTradingCanisterId={socialTradingCanisterId}
          currentUserId={currentUserId}
          userDisplayName={userDisplayName}
        />
        
        <div className="container mx-auto px-4 py-6 pb-24">
          <LiquidityDisplay />
          
          <div className="space-y-6">
            <LiveChart />
            <ExpirySelector />
            <OptionTypeSelector 
              selectedType={selectedType}
              onTypeChange={(type) => {
                setSelectedType(type);
                if (inWalkthrough && step === 4) {
                  setWalkthroughOptionSelected(true);
                }
              }}
            />
            <StrikeCards 
              selectedType={selectedType}
              selectedStrike={selectedStrike}
              onStrikeSelect={(strike) => {
                setSelectedStrike(strike);
                if (inWalkthrough && step === 5) {
                  setWalkthroughStrikeSelected(true);
                }
              }}
            />
            <OrderSection 
              selectedType={selectedType}
              selectedStrike={selectedStrike}
              quantity={quantity}
              onQuantityChange={setQuantity}
              onTrade={handleDemoTrade}
            />
          </div>
        </div>

        <BottomNav 
          activeTab={activeTab}
          onTabChange={(tabId) => {
            setActiveTab(tabId);
            // Handle specific tab actions
            switch (tabId) {
              case 'positions':
                setIsPositionDetailsOpen(true);
                break;
              case 'risk':
                setIsComingSoonOpen(true);
                setComingSoonFeature('Risk Management');
                break;
              case 'history':
                setIsHistoryOpen(true);
                break;
              case 'analysis':
                setIsComingSoonOpen(true);
                setComingSoonFeature('Advanced Analysis');
                break;
              default:
                break;
            }
          }}
          notifications={notifications}
        />
      </div>

      {isAutoHedgeOpen && (
        <AutoHedgeModal 
          isOpen={isAutoHedgeOpen}
          onClose={() => setIsAutoHedgeOpen(false)}
        />
      )}

      {isEducationalDemoOpen && (
        <EducationalDemo 
          isOpen={isEducationalDemoOpen}
          onClose={handleEducationalDemoClose}
        />
      )}

      {getFeatureFlag('SOCIAL_TRADING') && isTournamentJoinOpen && (
        <TournamentJoinModal 
          isOpen={isTournamentJoinOpen}
          onClose={() => setIsTournamentJoinOpen(false)}
          userDisplayName={userDisplayName}
        />
      )}

      {isPositionDetailsOpen && (
        <PositionDetailsModal 
          isOpen={isPositionDetailsOpen}
          onClose={() => setIsPositionDetailsOpen(false)}
        />
      )}

      {isHistoryOpen && (
        <HistoryModal 
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      {isComingSoonOpen && (
        <ComingSoonModal 
          isOpen={isComingSoonOpen}
          onClose={() => setIsComingSoonOpen(false)}
          feature={comingSoonFeature}
        />
      )}

      {isSocialTradingOpen && (
        <SocialTradingModal 
          isOpen={isSocialTradingOpen}
          onClose={() => setIsSocialTradingOpen(false)}
          userDisplayName={userDisplayName}
        />
      )}

      {isLeaderboardOpen && (
        <LeaderboardModal 
          isOpen={isLeaderboardOpen}
          onClose={() => setIsLeaderboardOpen(false)}
        />
      )}
        </TournamentProvider>
      </SocialTradingProvider>
    </>
  );
}

function App() {
  return (
    <PricingProvider>
      <DemoModeProvider>
        <AutoHedgeProvider>
          <AppContent />
        </AutoHedgeProvider>
      </DemoModeProvider>
    </PricingProvider>
  );
}

export default App;
